import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, signal } from '@angular/core';

/**
 * What the list reads from each document, in the shape the record detail endpoint
 * returns it.
 */
export interface ListedDocument {
  /**
   * Stable identity, for tracking the list.
   */
  pk?: string;

  /**
   * File name shown under the icon.
   */
  filename?: string;
}

/**
 * Documents block of the record detail modal, card
 * [86e33f66n](https://app.clickup.com/t/86e33f66n) (D8), after the wireframe 25:97.
 *
 * Handed to `ui-cash-flow` through `FINANCIAL_RECORD_DETAILS_DOCUMENTS_LIST_COMPONENT`, the
 * same token `serasa-cl-webcomponents` uses for its own list.
 *
 * ## Why a component of our own
 *
 * The wireframe draws an icon with the word "Adicionar" and each attached document as a
 * file icon with its name. The design system list writes "Adicionar documento" and a
 * large "DOC" thumbnail straight into its template, and neither changes by CSS. The first
 * attempts reshaped that list through overrides — a dashed 36x36 box, then 52x40 — and
 * the second one misread the wireframe: 52x40 is the whole control, icon and label, not a
 * dashed box. With the list ours, the markup is what the wireframe shows and the fragile
 * overrides go away.
 *
 * ## View only
 *
 * There is no delete button. Rule 7 of the parent card asks for documents to be viewed
 * only, as in Serasa, and the package list's "Excluir" would contradict it. `deletion`
 * still exists because the package subscribes to it, and it never emits.
 *
 * ## Why `@Input` and not `input()`
 *
 * The package does not bind these through a template: its `DocumentsListFactoryDirective`
 * creates the component and assigns `instance.documents = …` directly, re-creating it on
 * every change. A signal input cannot be assigned that way, so these stay plain inputs.
 */
@Component({
  selector: 'gn-documents-list',
  standalone: true,
  templateUrl: './documents-list.component.html',
  styleUrls: ['./documents-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentsListComponent {
  /**
   * Attached documents.
   */
  @Input() public documents: ListedDocument[] | null = [];

  /**
   * Whether an upload is in progress.
   */
  @Input() public loading: boolean | null = false;

  /**
   * Whether the add control is shown. The package decides it from its own configuration.
   */
  @Input() public showAddButton = true;

  /**
   * Part of the package contract. Unused here, since nothing can be deleted.
   */
  @Input() public validateDeletion = false;

  /**
   * Emits the file picked through "Adicionar".
   */
  @Output() public readonly newDocument = new EventEmitter<File>();

  /**
   * Emits the index of the document selected for viewing, or `null` when the selection
   * is cleared. The package opens and closes the viewer from it.
   */
  @Output() public readonly selection = new EventEmitter<number | null>();

  /**
   * Part of the package contract; never emits — see "View only" above.
   */
  @Output() public readonly deletion = new EventEmitter<number>();

  /**
   * Index of the document being viewed.
   */
  public readonly selectedIndex = signal<number | null>(null);

  /**
   * Hands the picked file on, and clears the input so picking the same file again still
   * fires `change`.
   *
   * @param event change event of the file input
   */
  public addFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file) {
      this.selectedIndex.set(null);
      this.newDocument.emit(file);
    }

    input.value = '';
  }

  /**
   * Selects a document for viewing, or clears the selection when it is already selected.
   *
   * @param index position of the document in the list
   */
  public select(index: number): void {
    const next = this.selectedIndex() === index ? null : index;

    this.selectedIndex.set(next);
    this.selection.emit(next);
  }
}
